import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Sigma, LineChart, Activity, Target, Bell, ExternalLink, ShieldAlert } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const CONTENT = {
  el: {
    breadcrumb_home: 'Αρχική',
    breadcrumb_features: 'Χαρακτηριστικά',
    title: 'Αλγόριθμοι',
    subtitle: 'Momentum Volatility ST Indicator — επιστημονική τεκμηρίωση',
    kicker: 'Ποσοτικό εργαλείο ανάλυσης',
    sections: [
      {
        icon: 'intro',
        heading: '1. Εισαγωγή',
        body: [
          'Ο Momentum Volatility ST Indicator είναι ένας ποσοτικός δείκτης που συνδυάζει τη μέτρηση της κατευθυντικής ορμής (momentum) με τη μέτρηση της μεταβλητότητας (volatility) των τιμών, με στόχο να παρέχει συστηματικές ποσοτικές ενδείξεις για μεταβολές τάσης σε επιλεγμένα χρηματοπιστωτικά μέσα (S&P 500, BTC).',
          'Δεν αποτελεί εργαλείο πρόβλεψης· επιδιώκει τη βελτίωση της σχέσης απόδοσης προς κίνδυνο μέσω μιας συνεπούς, αντικειμενικής μεθοδολογίας που φιλτράρει μέρος του θορύβου των τιμών.',
        ],
      },
      {
        icon: 'momentum',
        heading: '2. Μαθηματική ορμή (Momentum)',
        body: [
          'Η ορμή ορίζεται ως το ρυθμό μεταβολής της τιμής σε παράθυρο n περιόδων:',
        ],
        formula: {
          label: 'Absolute momentum',
          expr: 'M(t) = P(t) − P(t − n)',
        },
        formula2: {
          label: 'Relative momentum (%)',
          expr: 'M_rel(t) = ( P(t) − P(t − n) ) ⁄ P(t − n) × 100%',
        },
        after: 'Θετικές τιμές M(t) υποδεικνύουν ανοδική τάση· αρνητικές, καθοδική. Η επιλογή του n καθορίζει το horizon του δείκτη — μεγαλύτερα n φιλτράρουν βραχυπρόθεσμο θόρυβο.',
      },
      {
        icon: 'volatility',
        heading: '3. Μεταβλητότητα (Volatility)',
        body: [
          'Η μεταβλητότητα εκφράζει τη διακύμανση των τιμών γύρω από ένα κεντρικό μέτρο. Χρησιμοποιείται ως μέτρο ρίσκου, όχι απόδοσης.',
          'Ο δείκτης χρησιμοποιεί το log-return r_i = ln(P_i / P_{i-1}) ως βάση, ώστε η μεταβλητότητα να είναι scale-invariant και συγκρίσιμη μεταξύ διαφορετικών asset classes.',
        ],
      },
      {
        icon: 'stddev',
        heading: '4. Τυπική απόκλιση (Standard Deviation)',
        body: [
          'Η τυπική απόκλιση σ των log-returns σε παράθυρο N περιόδων:',
        ],
        formula: {
          label: 'Sample standard deviation',
          expr: 'σ = √( Σ (r_i − μ)² ⁄ (N − 1) )',
        },
        formula2: {
          label: 'Mean of returns',
          expr: 'μ = ( Σ r_i ) ⁄ N',
        },
        after: 'Η ετήσια τυπική απόκλιση προκύπτει με πολλαπλασιασμό με √(περίοδοι ετησίως) — 252 για ημερήσια δεδομένα ισοτιμιών ή S&P 500, 365 για κρυπτονομίσματα (24/7 αγορές).',
      },
      {
        icon: 'adaptive',
        heading: '5. Προσαρμοστική λογική ST',
        body: [
          'Το τμήμα ST (Systematic Threshold) της μεθοδολογίας συνδυάζει τα M(t) και σ σε έναν συνθετικό δείκτη με προσαρμοστικά κατώφλια. Το κατώφλι δεν είναι σταθερό αριθμός — αναπροσαρμόζεται δυναμικά ώστε να αντικατοπτρίζει το τρέχον επίπεδο μεταβλητότητας του υποκείμενου asset.',
          'Πρακτικά, σε περιόδους χαμηλής μεταβλητότητας τα κατώφλια στενεύουν (πιο ευαίσθητος δείκτης), ενώ σε περιόδους υψηλής μεταβλητότητας τα κατώφλια διευρύνονται (φίλτρο θορύβου). Αυτή η προσαρμογή φιλτράρει μέρος του θορύβου των τιμών και μειώνει τον αριθμό των false signals.',
        ],
      },
      {
        icon: 'signals',
        heading: '6. Παραγόμενα Σήματα',
        body: [
          'Παρά τις σύνθετες μαθηματικές και στατιστικές διεργασίες που εκτελούνται στο παρασκήνιο, ο Momentum Volatility ST Indicator έχει σχεδιαστεί ώστε να προσφέρει μια απλή και άμεσα αξιοποιήσιμη εμπειρία χρήσης.',
          'Ο αλγόριθμος συνδυάζει δυναμικά την ανάλυση της ορμής, της μεταβλητότητας και της στατιστικής διασποράς της αγοράς και μετατρέπει όλες αυτές τις πληροφορίες σε ένα μόνο, σαφές σήμα κατεύθυνσης.',
          'Ο χρήστης χρειάζεται να παρακολουθεί αποκλειστικά δύο τύπους σημάτων:',
        ],
        signals: [
          {
            type: 'long',
            label: 'Long Signal',
            desc: 'υποδηλώνει ότι οι τρέχουσες συνθήκες της αγοράς ευνοούν τη διατήρηση ή την έναρξη ανοδικής θέσης.',
          },
          {
            type: 'short',
            label: 'Short Signal',
            desc: 'υποδηλώνει ότι οι τρέχουσες συνθήκες της αγοράς ευνοούν τη διατήρηση ή την έναρξη καθοδικής θέσης.',
          },
        ],
        after: 'Ο χρήστης δεν απαιτείται να αναλύει πολύπλοκους μαθηματικούς υπολογισμούς ή να ερμηνεύει πολλαπλούς τεχνικούς δείκτες. Όλες οι ποσοτικές αναλύσεις πραγματοποιούνται αυτόματα από τον αλγόριθμο, ο οποίος παρουσιάζει μόνο το τελικό αποτέλεσμα με τη μορφή ενός ξεκάθαρου Long ή Short σήματος.',
      },
      {
        icon: 'tradingview',
        heading: '7. Πρόσβαση μέσω TradingView',
        body: [
          'Ο δείκτης είναι διαθέσιμος ως κλειστός Pine Script indicator στην πλατφόρμα TradingView. Οι συνδρομητές λαμβάνουν πρόσβαση στο TradingView username τους μέσα σε 24 ώρες από την ενεργοποίηση της συνδρομής.',
          'Ο δείκτης λειτουργεί σε όλα τα timeframes (1m έως 1W) αλλά έχει βελτιστοποιηθεί για το ημερήσιο (1D) γράφημα του BTC/USD και το τριήμερο (3D) γράφημα του S&P 500.',
          'Ο δείκτης έχει αναπτυχθεί και λειτουργεί πάνω σε Heikin-Ashi candlesticks, των οποίων ο μετασχηματισμός εξομαλύνει τις βραχυχρόνιες διακυμάνσεις της τιμής, αποσβένει μέρος του στοχαστικού θορύβου και επιτρέπει καθαρότερη εκτίμηση της υποκείμενης κατεύθυνσης και της δυναμικής της τάσης.',
        ],
        charts: [
          {
            src: 'https://customer-assets-wrfwihn1.emergentagent.net/job_d55a5905-f2f9-4823-ae56-0e4b4516ff30/artifacts/xq8kta5f_BTCUSD_image1.webp',
            alt: 'BTC/USD — Ημερήσιο (1D) γράφημα με σήματα Long/Short του Momentum Volatility ST Indicator',
            caption: 'BTC/USD · 1D — παραδείγματα σημάτων Long/Short από τον Momentum Volatility ST Indicator',
          },
          {
            src: 'https://customer-assets-wrfwihn1.emergentagent.net/job_d55a5905-f2f9-4823-ae56-0e4b4516ff30/artifacts/6nljjsss_SPX500USD_image1.webp',
            alt: 'S&P 500 — Τριήμερο (3D) γράφημα με σήματα Long/Short του Momentum Volatility ST Indicator',
            caption: 'S&P 500 · 3D — παραδείγματα σημάτων Long/Short από τον Momentum Volatility ST Indicator',
          },
        ],
      },
      {
        icon: 'disclaimer',
        heading: '8. Risk Management',
        body: [
          'Η αποτελεσματική αξιοποίηση ενός ποσοτικού αλγορίθμου δεν εξαρτάται αποκλειστικά από την ποιότητα των παραγόμενων σημάτων, αλλά και από την εφαρμογή αυστηρών αρχών διαχείρισης κινδύνου (Risk Management). Η μακροχρόνια στατιστική απόδοση ενός συστήματος συναλλαγών προϋποθέτει πειθαρχημένη κατανομή κεφαλαίου, ελεγχόμενη έκθεση στον κίνδυνο και συνεπή εφαρμογή των κανόνων διαχείρισης θέσεων.',
          'Στο πλαίσιο της μεθοδολογίας του Momentum Volatility ST Indicator, οι ιστορικές δοκιμές και η αξιολόγηση της στρατηγικής έχουν πραγματοποιηθεί με συντηρητικές παραμέτρους μόχλευσης. Για αγορές που ακολουθούν μακροχρόνια ανοδική δομή, όπως ο S&P 500, η στρατηγική έχει σχεδιαστεί αποκλειστικά για long θέσεις, με μέγιστη ενδεικτική μόχλευση 4×. Αντίστοιχα, για το Bitcoin, λόγω της σημαντικά υψηλότερης μεταβλητότητας, χρησιμοποιείται μέγιστη ενδεικτική μόχλευση 2×, με στόχο τη διατήρηση ελεγχόμενης έκθεσης στον κίνδυνο.',
          'Οι παραπάνω παράμετροι αποτελούν μέρος της ερευνητικής μεθοδολογίας του συστήματος και δεν υποκαθιστούν την προσωπική αξιολόγηση κινδύνου κάθε επενδυτή. Η απόδοση οποιασδήποτε στρατηγικής επηρεάζεται από παράγοντες όπως η μεταβλητότητα της αγοράς, η ρευστότητα, το κόστος συναλλαγών, το slippage και η συνεπής εφαρμογή των κανόνων διαχείρισης κεφαλαίου.',
        ],
      },
    ],
    ctaTitle: 'Απόκτησε πρόσβαση στο δείκτη',
    ctaDesc: 'Ο Momentum Volatility ST Indicator διατίθεται στους συνδρομητές του Basic Plan μέσω TradingView.',
    ctaBtn: 'Απόκτησε πρόσβαση στο TradingView',
  },
  en: {
    breadcrumb_home: 'Home',
    breadcrumb_features: 'Features',
    title: 'Algorithms',
    subtitle: 'Momentum Volatility ST Indicator — technical documentation',
    kicker: 'Quantitative analysis tool',
    sections: [
      {
        icon: 'intro',
        heading: '1. Introduction',
        body: [
          'The Momentum Volatility ST Indicator is a quantitative indicator combining directional momentum with price volatility to provide systematic quantitative signals on trend changes for selected financial instruments (S&P 500, BTC).',
          'It is not a prediction tool. It seeks to improve the return-to-risk relationship through a consistent, objective methodology that filters part of the price noise.',
        ],
      },
      {
        icon: 'momentum',
        heading: '2. Mathematical Momentum',
        body: ['Momentum is defined as the rate of price change over an n-period window:'],
        formula: { label: 'Absolute momentum', expr: 'M(t) = P(t) − P(t − n)' },
        formula2: { label: 'Relative momentum (%)', expr: 'M_rel(t) = ( P(t) − P(t − n) ) ⁄ P(t − n) × 100%' },
        after: 'Positive M(t) values indicate an uptrend; negative, a downtrend. The choice of n defines the horizon — larger n filter short-term noise.',
      },
      {
        icon: 'volatility',
        heading: '3. Volatility',
        body: [
          'Volatility expresses the dispersion of prices around a central measure. It is used as a risk metric, not a return metric.',
          'The indicator uses log-returns r_i = ln(P_i / P_{i-1}) as its base, so volatility remains scale-invariant and comparable across different asset classes.',
        ],
      },
      {
        icon: 'stddev',
        heading: '4. Standard Deviation',
        body: ['Standard deviation σ of log-returns over an N-period window:'],
        formula: { label: 'Sample standard deviation', expr: 'σ = √( Σ (r_i − μ)² ⁄ (N − 1) )' },
        formula2: { label: 'Mean of returns', expr: 'μ = ( Σ r_i ) ⁄ N' },
        after: 'Annualized standard deviation is obtained by multiplying by √(periods per year) — 252 for daily equities / S&P 500 data, 365 for cryptocurrencies (24/7 markets).',
      },
      {
        icon: 'adaptive',
        heading: '5. Adaptive ST Logic',
        body: [
          'The ST (Systematic Threshold) component combines M(t) and σ into a composite signal with adaptive thresholds. The threshold is not a fixed number — it is dynamically recalibrated to reflect the current volatility regime of the underlying asset.',
          'In practice, thresholds tighten in low-volatility regimes (more sensitive indicator) and widen in high-volatility regimes (noise filter). This adaptation filters part of the price noise and reduces the number of false signals.',
        ],
      },
      {
        icon: 'signals',
        heading: '6. Generated Signals',
        body: [
          'Despite the complex mathematical and statistical processes running in the background, the Momentum Volatility ST Indicator is designed to deliver a simple and immediately actionable user experience.',
          'The algorithm dynamically combines momentum analysis, volatility, and the statistical dispersion of the market, transforming all of that information into a single, clear directional signal.',
          'The user only needs to monitor two types of signals:',
        ],
        signals: [
          {
            type: 'long',
            label: 'Long Signal',
            desc: 'indicates that current market conditions favor holding or entering a long position.',
          },
          {
            type: 'short',
            label: 'Short Signal',
            desc: 'indicates that current market conditions favor holding or entering a short position.',
          },
        ],
        after: 'The user is not required to analyze complex mathematical calculations or interpret multiple technical indicators. All quantitative analysis is performed automatically by the algorithm, which presents only the final result as a clear Long or Short signal.',
      },
      {
        icon: 'tradingview',
        heading: '7. TradingView Access',
        body: [
          'The indicator is available as a closed-source Pine Script indicator on the TradingView platform. Subscribers receive access to their TradingView username within 24 hours of subscription activation.',
          'The indicator operates on all timeframes (1m to 1W) but is optimized for the daily (1D) BTC/USD chart and the 3-day (3D) S&P 500 chart.',
          'The indicator has been developed to run on Heikin-Ashi candlesticks. This transform smooths short-term price fluctuations, dampens part of the stochastic noise, and allows a cleaner assessment of the underlying direction and the strength of the trend.',
        ],
        charts: [
          {
            src: 'https://customer-assets-wrfwihn1.emergentagent.net/job_d55a5905-f2f9-4823-ae56-0e4b4516ff30/artifacts/xq8kta5f_BTCUSD_image1.webp',
            alt: 'BTC/USD — daily (1D) chart with Long/Short signals from the Momentum Volatility ST Indicator',
            caption: 'BTC/USD · 1D — sample Long/Short signals from the Momentum Volatility ST Indicator',
          },
          {
            src: 'https://customer-assets-wrfwihn1.emergentagent.net/job_d55a5905-f2f9-4823-ae56-0e4b4516ff30/artifacts/6nljjsss_SPX500USD_image1.webp',
            alt: 'S&P 500 — 3-day (3D) chart with Long/Short signals from the Momentum Volatility ST Indicator',
            caption: 'S&P 500 · 3D — sample Long/Short signals from the Momentum Volatility ST Indicator',
          },
        ],
      },
      {
        icon: 'disclaimer',
        heading: '8. Risk Management',
        body: [
          'The effective use of a quantitative algorithm does not depend solely on the quality of the signals it generates — it also depends on the disciplined application of Risk Management principles. Long-term statistical performance of a trading system requires disciplined capital allocation, controlled risk exposure, and consistent enforcement of position-management rules.',
          'Within the Momentum Volatility ST Indicator methodology, historical testing and strategy evaluation have been conducted with conservative leverage parameters. For markets that follow a long-term uptrend structure — such as the S&P 500 — the strategy is designed exclusively for long positions with a maximum indicative leverage of 4×. For Bitcoin, given its significantly higher volatility, a maximum indicative leverage of 2× is used, aiming to preserve controlled risk exposure.',
          'These parameters are part of the research methodology of the system and do not substitute for each investor\'s personal risk assessment. The performance of any strategy is influenced by factors such as market volatility, liquidity, transaction costs, slippage, and the consistency with which capital-management rules are applied.',
        ],
      },
    ],
    ctaTitle: 'Get access to the indicator',
    ctaDesc: 'The Momentum Volatility ST Indicator is available to Basic Plan subscribers via TradingView.',
    ctaBtn: 'Get TradingView access',
  },
};

const ICONS = {
  intro: Sigma,
  momentum: Activity,
  volatility: LineChart,
  stddev: Sigma,
  adaptive: Target,
  signals: Bell,
  tradingview: ExternalLink,
  disclaimer: ShieldAlert,
};

const Formula = ({ label, expr }) => (
  <div className="my-4 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 border border-cyan-500/20 rounded-lg p-4 md:p-5">
    <p className="text-cyan-400 text-xs uppercase tracking-widest mb-2">{label}</p>
    <p className="font-mono text-white text-base md:text-lg break-all leading-relaxed">{expr}</p>
  </div>
);

const AlgorithmsPage = () => {
  const { language } = useLanguage();
  const t = CONTENT[language] || CONTENT.en;

  return (
    <div className="min-h-screen bg-[#050811] text-white" data-testid="algorithms-page">
      <Navbar />

      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center text-sm text-white/50 mb-6" aria-label="breadcrumb">
            <Link to="/" className="hover:text-white/80">{t.breadcrumb_home}</Link>
            <ChevronRight size={14} className="mx-2" />
            <span>{t.breadcrumb_features}</span>
            <ChevronRight size={14} className="mx-2" />
            <span className="text-white/80">{t.title}</span>
          </nav>

          {/* Header */}
          <div className="inline-block px-3 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-5">
            <span className="text-cyan-400 text-xs font-semibold uppercase tracking-widest">{t.kicker}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-3 leading-tight" data-testid="algorithms-title">
            {t.title}
          </h1>
          <p className="text-lg text-white/70 mb-16">{t.subtitle}</p>

          {/* Sections */}
          <div className="space-y-14">
            {t.sections.map((s) => {
              const Icon = ICONS[s.icon] || Sigma;
              return (
                <section
                  key={s.heading}
                  className="scroll-mt-24"
                  data-testid={`algorithms-section-${s.icon}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center">
                      <Icon className="text-cyan-400" size={20} />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-semibold">{s.heading}</h2>
                  </div>
                  <div className="space-y-3 text-white/80 leading-relaxed">
                    {s.body && s.body.map((para) => (
                      <p key={para.slice(0, 30)}>{para}</p>
                    ))}
                  </div>
                  {s.formula && <Formula {...s.formula} />}
                  {s.formula2 && <Formula {...s.formula2} />}
                  {s.bullets && (
                    <ul className="mt-4 space-y-2 list-disc pl-6 text-white/80">
                      {s.bullets.map((b) => (
                        <li key={b.slice(0, 30)}>{b}</li>
                      ))}
                    </ul>
                  )}
                  {s.signals && (
                    <div className="mt-5 space-y-3" data-testid="algorithms-signals-list">
                      {s.signals.map((sig) => {
                        const isLong = sig.type === 'long';
                        return (
                          <div
                            key={sig.type}
                            className={`flex items-start gap-3 rounded-lg border p-4 ${
                              isLong
                                ? 'bg-emerald-500/5 border-emerald-500/30'
                                : 'bg-red-500/5 border-red-500/30'
                            }`}
                            data-testid={`algorithms-signal-${sig.type}`}
                          >
                            <span
                              className={`mt-0.5 inline-flex h-3 w-3 rounded-full shrink-0 ${
                                isLong ? 'bg-emerald-400' : 'bg-red-400'
                              }`}
                              aria-hidden="true"
                            />
                            <p className="text-white/85 leading-relaxed">
                              <span
                                className={`font-semibold ${
                                  isLong ? 'text-emerald-300' : 'text-red-300'
                                }`}
                              >
                                {sig.label}
                              </span>{' '}
                              — {sig.desc}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {s.after && <p className="text-white/80 leading-relaxed mt-3">{s.after}</p>}
                  {s.charts && s.charts.length > 0 && (
                    <div className="mt-6 space-y-6">
                      {s.charts.map((c) => (
                        <figure
                          key={c.src}
                          className="bg-white/[0.02] border border-white/10 rounded-xl overflow-hidden"
                          data-testid={`algorithms-chart-${c.src.split('_').slice(-2, -1)[0] || 'chart'}`}
                        >
                          <img
                            src={c.src}
                            alt={c.alt}
                            loading="lazy"
                            className="w-full h-auto block"
                          />
                          <figcaption className="text-white/60 text-xs md:text-sm px-4 py-3 border-t border-white/5 text-center">
                            {c.caption}
                          </figcaption>
                        </figure>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>

          {/* CTA */}
          <div
            className="mt-20 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-2xl p-8 md:p-10 text-center"
            data-testid="algorithms-cta"
          >
            <h3 className="text-2xl md:text-3xl font-semibold mb-3">{t.ctaTitle}</h3>
            <p className="text-white/70 mb-6 max-w-xl mx-auto">{t.ctaDesc}</p>
            <Button
              asChild
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold px-8"
              data-testid="algorithms-cta-btn"
            >
              <Link to="/#pricing">{t.ctaBtn}</Link>
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AlgorithmsPage;
